import get from 'lodash/get';
import isArray from 'lodash/isArray';
import omit from 'lodash/omit';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/operator/combineLatest';
import 'rxjs/add/operator/map';
import {Observable} from 'rxjs/Observable';
import {isVisibleItem} from '../../_libs/redux-observable-list';
import {buildAssetProjectLink$, buildOrganizationLink$, buildProjectLink$} from '../../app/navigation/utils';
import {getCollection, getId, getLink} from '../../core-utilities/domain/ReduxUtils';
import {BreadcrumbType} from '../../core-utilities/enums/BreadcrumbType';
import {sortByProperty} from '../../core-utilities/filters/filterUtils';
import {getStateParamsInView$} from '../../core-utilities/redux';
import {MILESTONE_COLLECTION} from '../../projects/domain/config';
import {PLACEHOLDER_MILESTONE} from '../../projects/domain/MilestoneDomain';
import {ASSET_TASK_COLLECTION, TASK_COLLECTION} from '../domain/config';
import {TaskType} from '../enums/TaskType';
import {getAssetTask, getProjectTask, getTemplateTask} from '../reducers/state';
import {assetTasksStates, projectTasksStates, templateTasksStates} from '../router-config';
import {assetTaskRouterState} from './assetTaskRouterState';
import {projectTaskRouterState} from './projectTaskRouterState';
import {templateTaskRouterState} from './templateTaskRouterState';


/**
 * @param {TaskType} taskType
 * @param {Observable<any>} state$
 * @param {Observable<any>} props$
 * @return {{stateParams$: Observable<any>, task$: Observable<any>, breadcrumbPath$: Observable<Array<NavigationBreadcrumbPath>>}}
 */
export function buildTaskPropsByType(taskType, state$, props$) {
  switch (taskType) {
    case TaskType.ASSET:
      return buildAssetTaskProps(state$, props$, taskType);

    case TaskType.PROJECT:
      return buildProjectTaskProps(state$, props$, taskType);

    case TaskType.TEMPLATE:
      return buildTemplateTaskProps(state$, props$, taskType);
  }
}

export function buildProjectTaskProps(state$, props$, taskType) {
  return buildTaskProps({
    state$,
    props$,
    taskType,
    routerStates: projectTasksStates,
    stateNames: projectTaskRouterState.options.stateNames,
    getItemState: getProjectTask,
  });
}

export function buildTemplateTaskProps(state$, props$, taskType) {
  return buildTaskProps({
    state$,
    props$,
    taskType,
    routerStates: templateTasksStates,
    stateNames: templateTaskRouterState.options.stateNames,
    getItemState: getTemplateTask,
  });
}

export function buildAssetTaskProps(state$, props$, taskType) {
  return buildTaskProps({
    state$,
    props$,
    taskType,
    routerStates: assetTasksStates,
    stateNames: assetTaskRouterState.options.stateNames,
    getItemState: getAssetTask,
  });
}

/**
 * @param {Observable<any>} state$
 * @param {Observable<any>} props$
 * @param {TaskType} taskType
 * @param {RouterTaskStates} routerStates
 * @param {Array<String>} stateNames
 * @param getItemState
 * @return {{stateParams$: Observable<any>, task$: Observable<any>, breadcrumbPath$: Observable<Array<NavigationBreadcrumbPath>>}}
 */
function buildTaskProps({state$, props$, taskType, routerStates, stateNames, getItemState}) {
  const stateParams$ = getStateParamsInView$(state$, stateNames)
    .distinctUntilChanged();

  const task$ = state$
    .map(getItemState)
    .pluck('item');

  const breadcrumbPath$ = buildBreadcrumbPath$({
    state$,
    taskType,
    routerStates,
    task$,
  });

  return {
    stateParams$,
    task$,
    breadcrumbPath$,
  };
}

/**
 * @param {Observable<any>} state$
 * @param {TaskType} taskType
 * @param {RouterTaskStates} routerStates
 * @param {Observable<any>} task$
 * @return {Observable<Array<NavigationBreadcrumbPath>>}
 */
function buildBreadcrumbPath$({state$, taskType, routerStates, task$}) {
  const taskLink$ = task$
    .map(task => !task ? {} : {
      id: getId(task),
      label: task.name,
      type: BreadcrumbType.task,
      to: routerStates.requirements,
      toParams: {taskId: getId(task)},
    });

  if (taskType === TaskType.ASSET) {
    return Observable
      .combineLatest(
        buildOrganizationLink$(state$),
        buildProjectLink$(state$),
        buildAssetProjectLink$(state$),
        taskLink$,
        (orgLink, projectLink, assetProjectLink, taskLink) => [orgLink, projectLink, assetProjectLink, taskLink]
      );
  } else {
    return Observable
      .combineLatest(
        buildOrganizationLink$(state$),
        buildProjectLink$(state$),
        taskLink$,
        (orgLink, projectLink, taskLink) => [orgLink, projectLink, taskLink]
      );
  }
}

/**
 * Separates milestones and tasks, sorts each set separately, then recombines them in a sorted grouping.
 * @param {ReduxObservableListOptions} options
 * @param {ReduxObservableListState} state
 */
export function listToArray(options, state) {
  const list = state.list;
  const taskList = [];
  const milestoneList = [];
  const milestoneMap = {};
  for (const key in list) {
    if (list.hasOwnProperty(key) && isVisibleItem(list[key])) {
      const item = list[key];
      switch (getCollection(item)) {
        case ASSET_TASK_COLLECTION:
        case TASK_COLLECTION:
          taskList.push(item);
          break;

        case MILESTONE_COLLECTION:
          milestoneList.push(item);
          milestoneMap[key] = item;
      }
    }
  }

  const sortedTaskList = applySortOptions(taskList, state.sortOptions);
  const sortedMilestoneList = applySortOptions(milestoneList, state.sortOptions);

  return integrateMilestones(sortedTaskList, sortedMilestoneList, milestoneMap);
}

// TODO: update to use new sort system when it goes live
export function applySortOptions(list) {
  return list.sort(sortByProperty('name', true, true));
}

/**
 * @param {Array<Object>} taskList
 * @param {Array<Object>} milestoneList
 * @param {Object<String, Object>} milestoneMap
 * @return {Array}
 */
export function integrateMilestones(taskList, milestoneList, milestoneMap) {
  const msGroups = {};

  // group each task according to its milestone
  for (let taskIndex = 0; taskIndex < taskList.length; taskIndex++) {
    const id = getLink(taskList[taskIndex], 'milestone').id || PLACEHOLDER_MILESTONE.id;

    if (!isArray(msGroups[id])) msGroups[id] = [];

    msGroups[id].push(taskList[taskIndex]);
  }

  const resultList = [];

  for (let msIndex = 0; msIndex < milestoneList.length; msIndex++) {
    const msId = milestoneList[msIndex].id;

    // Skip placeholder milestone
    if (msId !== PLACEHOLDER_MILESTONE.id) {
      const msHasTasks = isArray(msGroups[msId]);
      const isDeleting = msId !== PLACEHOLDER_MILESTONE.id && milestoneMap[msId].isDeleting;

      // add the milestone to the results array
      if (!isDeleting) {
        resultList.push(milestoneMap[msId]);
      }

      // add the tasks under that milestone to the results array
      if (msHasTasks && !isDeleting) {
        resultList.push(...msGroups[msId]);
      }
    }
  }

  // insert placeholder milestone
  if (milestoneMap.hasOwnProperty(PLACEHOLDER_MILESTONE.id)) {
    const msId = PLACEHOLDER_MILESTONE.id;
    const msHasTasks = isArray(msGroups[msId]);
    resultList.push(milestoneMap[msId]);

    // add the tasks under that milestone to the results array
    if (msHasTasks) {
      resultList.push(...msGroups[msId]);
    }
  }

  return resultList;
}

/**
 * @param {ReduxList} reduxList
 */
export function buildReducers(reduxList) {
  const baseReducer = reduxList.reducers.reducer;
  const initialState = reduxList.reducers.initialState;
  return function (state = initialState, action) {
    switch (action.type) {
      case reduxList.actions.initList.TYPE:
      case reduxList.actions.destroyList.TYPE:
        return baseReducer(state, action);

      case reduxList.actions.applyFilter.TYPE:
        return applyFilterReducer(state, action);

      default:
        // This may be overkill. May need to cut down the actions we check more.
        return applyFilterToNewList(state, action);
    }
  };

  function applyFilterReducer(state, action) {
    let nextState = baseReducer(state, action);

    if (get(state, 'filterOptions.milestone.id') !== get(action.payload, 'filterOptions.milestone.id')) {
      if (state.list !== nextState.list) {
        // since the list is already new, we do not need to create a new one to maintain immutability.
        updateMilestones(nextState.list, nextState);
      } else {
        // Ensure we are not mutating the state, just in case the base reducer did not make any changes.
        if (state === nextState) {
          nextState = {
            ...state,
          };
        }

        // the list did not change, make a new one so that we do not mutate the list.
        const list = {
          ...nextState.list,
        };
        nextState.list = list;

        updateMilestones(list, action.payload);
      }
    }

    return nextState;
  }

  function applyFilterToNewList(state, action) {
    const nextState = baseReducer(state, action);

    if (state.list !== nextState.list) {
      // since the list is already new, we do not need to create a new one.
      updateMilestones(nextState.list, nextState);
    }

    return nextState;
  }

  /**
   * Mutates list input to allow for performance optimization.
   * We only need to create a list once per reducer call.
   * @param list Mutated
   * @param filterData
   */
  function updateMilestones(list, filterData) {
    const milestoneFilter = get(filterData, 'filterOptions.milestone.id', {});
    const filterAllMilestones = get(milestoneFilter, 'all', false);

    for (const key in list) {
      if (list.hasOwnProperty(key)) {
        const item = list[key];
        switch (getCollection(item)) {
          case MILESTONE_COLLECTION:
            list[key] = {
              ...item,
              isCollapsed: (filterAllMilestones && milestoneFilter[key] !== false)
                || (!filterAllMilestones && milestoneFilter[key] === true),
            };
        }
      }
    }
  }
}

export function isVisibleByMilestone(item, filterData) {
  const milestoneIdFilter = filterData.filterOptions.milestone.id;
  const milestoneId = getMilestoneId(item);
  if ((milestoneIdFilter.all && milestoneIdFilter[milestoneId] !== false)
    || (!milestoneIdFilter.all && milestoneIdFilter[milestoneId] === true))
    return false;
  return true;
}

export function getMilestoneId(task) {
  return getLink(task, 'milestone').id || PLACEHOLDER_MILESTONE.id;
}


export function updateMilestoneIdFilter(id, filterData) {
  const milestoneIdFilter = filterData.filterOptions.milestone.id;

  if (id === 'all') {
    if (milestoneIdFilter.all) {
      return cloneMilestoneIdFilter(filterData, {});

    } else {
      return cloneMilestoneIdFilter(filterData, {all: true});

    }

  } else if (milestoneIdFilter.hasOwnProperty('all')) {
    if (milestoneIdFilter.hasOwnProperty(id)) {
      return cloneMilestoneIdFilter(filterData, omit(milestoneIdFilter, [id]));

    } else {
      return cloneMilestoneIdFilter(filterData, {
        ...milestoneIdFilter,
        [id]: false,
      });
    }

  } else if (milestoneIdFilter.hasOwnProperty(id)) {
    return cloneMilestoneIdFilter(filterData, omit(milestoneIdFilter, [id]));

  } else {
    return cloneMilestoneIdFilter(filterData, {
      ...milestoneIdFilter,
      [id]: true,
    });
  }
}

function cloneMilestoneIdFilter(filterData, id) {
  return {
    ...filterData.filterOptions,
    milestone: {
      ...filterData.filterOptions.milestone,
      id,
    },
  };
}



// WEBPACK FOOTER //
// ./src/js/tasks/redux/utils.js